/** Direct browser calls to PayMongo using the publishable key only (PCI: card never hits Digisaka API). */

const PAYMONGO_V1 = 'https://api.paymongo.com/v1';

function authHeader(publicKey: string): string {
  return `Basic ${btoa(`${publicKey.trim()}:`)}`;
}

function formatPayMongoErrors(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return 'PayMongo error';
  const errors = (payload as { errors?: unknown }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const parts = errors.map((e) => {
      if (e && typeof e === 'object' && 'detail' in e) return String((e as { detail: unknown }).detail);
      return JSON.stringify(e);
    });
    return parts.join('; ');
  }
  const detail = (payload as { detail?: unknown }).detail;
  if (typeof detail === 'string') return detail;
  return JSON.stringify(payload);
}

async function payMongoPost(publicKey: string, path: string, body: unknown): Promise<any> {
  const res = await fetch(`${PAYMONGO_V1}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader(publicKey),
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatPayMongoErrors(json));
  return json;
}

/** Digits-only PAN (spaces & formatting allowed in UI). Unicode digits normalized when possible. */
export function sanitizeCardDigits(pan: string): string {
  try {
    return pan.normalize('NFKC').replace(/\D/g, '');
  } catch {
    return pan.replace(/\D/g, '');
  }
}

/** Parse expiry "mm/yyyy" or "mm/yy" → month 1–12, four-digit year. */
export function parseCardExpiry(mmYyyy: string): { expMonth: number; expYear: number } | null {
  const t = mmYyyy.trim();
  const m = t.match(/^(\d{1,2})\s*\/\s*(\d{2}|\d{4})$/);
  if (!m) return null;
  const month = Math.min(12, Math.max(1, parseInt(m[1], 10)));
  let year = parseInt(m[2], 10);
  if (year < 100) year += 2000;
  return { expMonth: month, expYear: year };
}

function paymongoBillingAddressPayload(addr: {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postal_code: string;
  country: string;
}): Record<string, string> {
  const out: Record<string, string> = {
    line1: addr.line1.trim(),
    city: addr.city.trim(),
    postal_code: addr.postal_code.trim(),
    country: addr.country.trim().toUpperCase(),
  };
  const l2 = addr.line2?.trim();
  if (l2) out.line2 = l2;
  const st = addr.state?.trim();
  if (st) out.state = st;
  return out;
}

export async function paymongoCreateCardPaymentMethod(publicKey: string, args: {
  cardDigits: string;
  expMonth: number;
  expYear: number;
  cvc: string;
  billingName: string;
  billingEmail?: string;
  billingPhone?: string;
  billingAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postal_code: string;
    country: string;
  };
}): Promise<string> {
  const card_number = sanitizeCardDigits(args.cardDigits);
  if (card_number.length < 12 || card_number.length > 19 || ![...card_number].every((c) => c >= '0' && c <= '9')) {
    throw new Error('Invalid card number (use 12–19 digits; test mode: PayMongo-provided test cards).');
  }
  const billing: Record<string, unknown> = {
    name: args.billingName.trim(),
    ...(args.billingEmail?.trim() ? { email: args.billingEmail.trim() } : {}),
    ...(args.billingPhone?.trim() ? { phone: args.billingPhone.trim() } : {}),
    ...(args.billingAddress
      ? { address: paymongoBillingAddressPayload(args.billingAddress) }
      : {}),
  };
  const payload = {
    data: {
      attributes: {
        type: 'card',
        details: {
          card_number,
          exp_month: args.expMonth,
          exp_year: args.expYear,
          cvc: args.cvc.replace(/\s/g, '').trim(),
        },
        billing,
      },
    },
  };
  const json = await payMongoPost(publicKey, '/payment_methods', payload);
  const id = json?.data?.id;
  if (typeof id !== 'string' || !id.startsWith('pm_')) {
    throw new Error('Unexpected PayMongo payment method response');
  }
  return id;
}

export async function paymongoAttachPaymentIntent(
  publicKey: string,
  paymentIntentId: string,
  paymentMethodId: string,
  clientKey: string,
): Promise<any> {
  return payMongoPost(
    publicKey,
    `/payment_intents/${encodeURIComponent(paymentIntentId)}/attach`,
    {
      data: {
        attributes: {
          payment_method: paymentMethodId,
          client_key: clientKey,
        },
      },
    },
  );
}

export type PayMongoCardCompleteResult =
  | { status: 'redirect'; url: string }
  | { status: 'success'; message: string; payment_method_id: string }
  | { status: 'pending'; message: string; payment_method_id: string };

/** Create payment method + attach to intent (after backend created the PaymentIntent). */
export async function paymongoCompleteCardPayment(
  publicKey: string,
  intent: { payment_intent_id: string; client_key: string },
  card: {
    cardNumberVis: string;
    expiry: string;
    cvc: string;
    nameOnCard: string;
    billingEmail?: string;
    billingAddress: {
      line1: string;
      line2?: string;
      city: string;
      state?: string;
      postal_code: string;
      country: string;
    };
    billingPhone?: string;
  },
): Promise<PayMongoCardCompleteResult> {
  const trimmedCvc = card.cvc.replace(/\s/g, '').trim();
  const digitsRaw = sanitizeCardDigits(card.cardNumberVis);
  if (digitsRaw.length < 12 || digitsRaw.length > 19)
    throw new Error('Enter a card number with 12–19 digits (in test mode use numbers from PayMongo’s docs).');
  if (![...digitsRaw].every((ch) => ch >= '0' && ch <= '9'))
    throw new Error('Card number must be digits only (avoid letters or placeholders — test cards fail if malformed).');

  const parsed = parseCardExpiry(card.expiry);
  if (!parsed) throw new Error('Use expiry as mm/yyyy (e.g. 12/2030).');
  if (!trimmedCvc || trimmedCvc.length < 3) throw new Error('Enter the security code (CVC).');
  if (!card.nameOnCard.trim()) throw new Error('Enter the name on the card.');

  const pmId = await paymongoCreateCardPaymentMethod(publicKey, {
    cardDigits: digitsRaw,
    expMonth: parsed.expMonth,
    expYear: parsed.expYear,
    cvc: trimmedCvc,
    billingName: card.nameOnCard,
    billingEmail: card.billingEmail,
    billingPhone: card.billingPhone,
    billingAddress: card.billingAddress,
  });

  const attached = await paymongoAttachPaymentIntent(
    publicKey,
    intent.payment_intent_id,
    pmId,
    intent.client_key,
  );
  const attrs = attached?.data?.attributes ?? {};
  const next = attrs.next_action;
  const redirectUrl =
    next?.type === 'redirect' && typeof next.redirect?.url === 'string'
      ? next.redirect.url
      : typeof (next as { redirect_url?: string } | undefined)?.redirect_url === 'string'
        ? (next as { redirect_url: string }).redirect_url
        : null;
  if (redirectUrl) return { status: 'redirect', url: redirectUrl };

  const st = attrs.status as string | undefined;
  if (st === 'succeeded') {
    return { status: 'success', message: 'Payment succeeded.', payment_method_id: pmId };
  }
  if (st) {
    return {
      status: 'pending',
      message: `Payment intent status: ${st}. Check PayMongo or your bank if needed.`,
      payment_method_id: pmId,
    };
  }
  return {
    status: 'pending',
    message: 'Payment submitted. Confirm status in PayMongo.',
    payment_method_id: pmId,
  };
}
